import { createFileRoute, notFound } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { useReplayBoardChrome } from "#/components/replay/ReplayBoard";
import { ReplayGameCard } from "#/components/replay/ReplayGameCard";
import { useReplayPlaybackHotkeys } from "#/components/replay/useReplayPlaybackHotkeys";
import { useReplayPlaybackSpeed } from "#/components/replay/useReplayPlaybackSpeed";
import { useReplayPlaybackStepTimer } from "#/components/replay/useReplayPlaybackStepTimer";
import { BoardImagesGate } from "#/game/BoardImagesGate";
import { GameStoreProvider, useGameSelector, useGameStoreApi } from "#/game/GameStoreProvider";
import { seekReplayDay } from "#/game/store/gameMutations";
import { beginReplayPlayback } from "#/game/store/gameMutations/playback";
import { DOCUMENT_TITLES, documentHead } from "#/lib/documentTitle";
import { replayIdRouteParams } from "#/lib/routeParams";
import { loadReplay, type ReplayPageData } from "#/server/game/replayPage";

export const Route = createFileRoute("/replay_/$replayId")({
  params: replayIdRouteParams,
  loader: async ({ params: { replayId } }) => {
    const replay = await loadReplay({ data: { replayId } });
    if (!replay) {
      throw notFound();
    }
    return replay;
  },
  head: () => documentHead(DOCUMENT_TITLES.replay, { robots: "noindex, nofollow" }),
  component: ReplayPage,
});

function ReplayPage() {
  const replay = Route.useLoaderData();

  return (
    <BoardImagesGate state={replay.initialState} mapId={replay.mapId}>
      <GameStoreProvider initialState={replay.initialState} initialServerDayNumber={1}>
        <ReplaySession replay={replay} />
      </GameStoreProvider>
    </BoardImagesGate>
  );
}

function restartReplay({
  api,
  replay,
  setPaused,
}: {
  api: ReturnType<typeof useGameStoreApi>;
  replay: ReplayPageData;
  setPaused: (paused: boolean) => void;
}) {
  setPaused(false);
  api.setState((state) =>
    beginReplayPlayback({
      state,
      initialState: replay.initialState,
      moves: replay.moves,
      finalServerDayNumber: replay.finalServerDayNumber,
    }),
  );
}

function seekReplayDayAndPause({
  api,
  direction,
  setPaused,
}: {
  api: ReturnType<typeof useGameStoreApi>;
  direction: "previous" | "next";
  setPaused: (paused: boolean) => void;
}) {
  setPaused(true);
  api.setState((state) => seekReplayDay({ state, direction }));
}

function useReplaySessionControls(replay: ReplayPageData) {
  const api = useGameStoreApi();
  const chrome = useReplayBoardChrome();
  const aiPlayback = useGameSelector((s) => s.aiPlayback);
  const [paused, setPaused] = useState(false);
  const handleTogglePause = useCallback(() => setPaused((value) => !value), []);
  const { setNormalSpeed, setFastSpeed } = useReplayPlaybackSpeed();

  useReplayPlaybackStepTimer({ paused });
  useReplayPlaybackHotkeys({
    canPause: Boolean(aiPlayback),
    onTogglePause: handleTogglePause,
    onSetNormalSpeed: setNormalSpeed,
    onSetFastSpeed: setFastSpeed,
  });

  const startPlayback = useCallback(() => {
    restartReplay({ api, replay, setPaused });
  }, [api, replay]);

  const seekDay = useCallback(
    (direction: "previous" | "next") => {
      seekReplayDayAndPause({ api, direction, setPaused });
    },
    [api],
  );

  useEffect(() => {
    if (replay.locked) return;
    startPlayback();
  }, [replay.locked, startPlayback]);

  return { chrome, paused, aiPlayback, handleTogglePause, startPlayback, seekDay };
}

function ReplaySession({ replay }: { replay: ReplayPageData }) {
  const { chrome, paused, aiPlayback, handleTogglePause, startPlayback, seekDay } =
    useReplaySessionControls(replay);

  return (
    <main className="flex w-full min-w-0 grow flex-col items-center">
      <ReplayGameCard
        locked={replay.locked}
        mapId={replay.mapId}
        playerId={replay.playerId}
        displayName={replay.displayName}
        mapDate={replay.mapDate}
        variant={replay.variant}
        boardScrollFocus={chrome.boardScrollFocus}
        scrollChrome={chrome.scrollChrome}
        topDockRef={chrome.topDockRef}
        bottomDockRef={chrome.bottomDockRef}
        paused={paused}
        canPause={Boolean(aiPlayback)}
        onTogglePause={handleTogglePause}
        onRestart={startPlayback}
        onPreviousDay={() => seekDay("previous")}
        onNextDay={() => seekDay("next")}
      />
    </main>
  );
}
